/**
 * Core hook for managing gameplay sessions.
 * 
 * This hook handles the complete lifecycle of a gameplay session:
 * - Session creation/retrieval (with on-demand localStorage migration)
 * - Event dispatching (guesses, submissions, hints)
 * - State synchronization between server and localStorage
 * - Analytics tracking (game start, resume, completion)
 * - Streak updates on game completion
 * - Gettoncini balance updates
 * 
 * Usage:
 * ```javascript
 * const {
 *   state,
 *   status,
 *   gameOver,
 *   won,
 *   isLoading,
 *   dispatchEvent,
 *   dispatchHintEvent,
 * } = useGameplaySession('cloze', date);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { 
  createOrGetSession, 
  sendGameplayEvent,
  getSession,
} from '../api/gameplay';
import { hasFreeAnonymousHints } from '../utils/anonymousHints';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import { useGameStatusesContext } from '../contexts/GameStatusesContext';
import { useMilestones } from '../contexts/MilestoneContext';
import { getLocalData, setLocalData } from '../util';
import { GameStatus, isGameOver } from '../types/gameplay';
import { invalidatePlayerGameStats } from '../utils/gameStatsCache';
import { useGettoncini } from '../contexts/GettonciniContext';
import { useLearnedTerms } from '../contexts/LearnedTermsContext';
import { useStelline } from '../contexts/StellineContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import { useGameStartAccountOffer } from '../contexts/GameStartAccountOfferContext';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import { useAds } from '../contexts/AdsContext';
import { useGameStreak } from './useGameStreak';
import { useStreak } from '../contexts/StreakContext';
import normalizeHintResponse from './normalizeHintResponse';
import { buildResourceAlertsFromPayload, queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import { getStreakCounterBefore, RESOURCE_BEAT_SPACING_MS } from '../utils/resourceCelebrationTiming';
import {
  extractGettonciniResource,
  extractLearnedTermsResource,
  extractStellineResource,
  extractStreakResource,
} from '../utils/appResources';
import { 
  logGameEvent, 
  trackGameHintInsufficientBalance,
  useGameSession,
  trackGameStart, 
  trackGameResume, 
  trackGameCompletion, 
  trackGameOver,
  getUserId
} from '../analytics';

// Must stay aligned with LOSS_REVEAL_STEP_MS in SerpentoniGame.js.
const SERPENTONI_LOSS_REVEAL_STEP_MS = 1500;
const SESSION_INIT_SHARED_CACHE_TTL_MS = 10000;
const COMPLETION_WIN_CELEBRATION_DELAY_MS = 3400;
const COMPLETION_RESOURCE_FIRST_BEAT_DELAY_MS = 900;
const COMPLETION_RESOURCE_ALERT_TRAILING_GAP_MS = 1600;
const sharedSessionInitPromises = new Map();

const getOrCreateSharedSessionInitPromise = (cacheKey, factory) => {
  const existingPromise = sharedSessionInitPromises.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = Promise.resolve().then(factory);
  sharedSessionInitPromises.set(cacheKey, promise);

  const clearCacheEntry = () => {
    setTimeout(() => {
      if (sharedSessionInitPromises.get(cacheKey) === promise) {
        sharedSessionInitPromises.delete(cacheKey);
      }
    }, SESSION_INIT_SHARED_CACHE_TTL_MS);
  };

  promise.then(clearCacheEntry).catch(() => {
    if (sharedSessionInitPromises.get(cacheKey) === promise) {
      sharedSessionInitPromises.delete(cacheKey);
    }
  });

  return promise;
};

export const __testables = {
  getOrCreateSharedSessionInitPromise,
  sharedSessionInitPromises,
  SESSION_INIT_SHARED_CACHE_TTL_MS,
};

const getCompletionAlertDelayMs = (gameSlug, newStatus, resultData = {}, stateData = {}) => {
  if (gameSlug !== 'serpentoni' || newStatus !== GameStatus.LOST) {
    return 0;
  }

  const totalWords = Number(resultData?.total_words);
  const foundWordsCount = Number(resultData?.found_words_count);
  const spangramFound = resultData?.spangram_found === true || stateData?.spangram_found === true;

  if (!Number.isInteger(totalWords) || !Number.isInteger(foundWordsCount)) {
    return 0;
  }

  const missingWords = Math.max(0, totalWords - foundWordsCount);
  const revealSteps = missingWords + (spangramFound ? 0 : 1);
  return revealSteps * SERPENTONI_LOSS_REVEAL_STEP_MS;
};

const mapStreakResourceToState = (resource) => {
  if (!resource || typeof resource !== 'object' || Array.isArray(resource)) {
    return null;
  }

  return {
    currentStreak: resource.current_streak || 0,
    longestStreak: resource.longest_streak || 0,
    lastPlayedDate: resource.last_played_date || null,
    streakStartDate: resource.streak_start_date || null,
    completedDatesLast7Days: Array.isArray(resource.completed_dates_last_7_days)
      ? resource.completed_dates_last_7_days
      : null,
    completedDatesCurrentWeek: Array.isArray(resource.completed_dates_current_week)
      ? resource.completed_dates_current_week
      : null,
  };
};

const getTrackingStartTimeAtCompletion = (gameStartTime, completionTimestamp) => {
  if (typeof gameStartTime !== 'number' || typeof completionTimestamp !== 'number') {
    return gameStartTime;
  }

  const elapsedAtCompletion = completionTimestamp - gameStartTime;
  if (elapsedAtCompletion < 0) {
    return gameStartTime;
  }

  return Date.now() - elapsedAtCompletion;
};

const buildCompletionResourceSchedule = ({
  initialDelayMs = 0,
  shouldWaitForWinCelebration = false,
  shouldCelebrateStreak = false,
  shouldCelebrateStelline = false,
  shouldCelebrateLearnedTerms = false,
}) => {
  const baseDelayMs = Math.max(0, Number(initialDelayMs) || 0)
    + (shouldWaitForWinCelebration ? COMPLETION_WIN_CELEBRATION_DELAY_MS : 0);
  const flightDelays = {};
  let nextBeatDelayMs = baseDelayMs + COMPLETION_RESOURCE_FIRST_BEAT_DELAY_MS;

  if (shouldCelebrateStreak) {
    flightDelays.streak = nextBeatDelayMs;
    nextBeatDelayMs += RESOURCE_BEAT_SPACING_MS;
  }

  if (shouldCelebrateStelline) {
    flightDelays.stelline = nextBeatDelayMs;
    nextBeatDelayMs += RESOURCE_BEAT_SPACING_MS;
  }

  if (shouldCelebrateLearnedTerms) {
    flightDelays.learnedTerms = nextBeatDelayMs;
    nextBeatDelayMs += RESOURCE_BEAT_SPACING_MS;
  }

  const hasFlights = shouldCelebrateStreak || shouldCelebrateStelline || shouldCelebrateLearnedTerms;
  return {
    flightDelays,
    alertDelayMs: hasFlights
      ? nextBeatDelayMs + COMPLETION_RESOURCE_ALERT_TRAILING_GAP_MS
      : baseDelayMs + 2000,
  };
};

/**
 * Core hook for managing gameplay sessions.
 * Handles session creation, event dispatching, state synchronization, and analytics.
 * 
 * @param {string} gameSlug - Game identifier (e.g., 'cloze', 'scalinata')
 * @param {string} date - Puzzle date (YYYY-MM-DD)
 * @param {object} options - Configuration options
 * @param {boolean} options.useServerState - Whether to use server-side session (default: true)
 * @param {function} options.deriveStatusFromLocalStorage - Function to derive status from localStorage data
 * 
 * @returns {object} Session state and actions
 * @returns {string|null} returns.sessionId - Session UUID
 * @returns {object|null} returns.puzzleConfig - Puzzle configuration from server
 * @returns {object|null} returns.state - Current game state
 * @returns {string} returns.status - Current game status (GameStatus enum value)
 * @returns {boolean} returns.gameOver - Whether the game has ended
 * @returns {boolean} returns.won - Whether the game was won
 * @returns {boolean} returns.lost - Whether the game was lost
 * @returns {boolean} returns.isLoading - Whether session is being initialized
 * @returns {string|null} returns.error - Error message if initialization failed
 * @returns {boolean} returns.isEventPending - Whether an event is being processed
 * @returns {function} returns.dispatchEvent - Function to send a gameplay event
 * @returns {function} returns.dispatchHintEvent - Function to send a hint event (with auth check)
 * @returns {function} returns.updateLocalState - Function to manually update local state
 * @returns {boolean} returns.isSignedIn - Whether user is authenticated
 */
export function useGameplaySession(gameSlug, date, options = {}) {
  const { 
    useServerState = true,
    deriveStatusFromLocalStorage = null,
    puzzleInstanceIdOverride = null,
    persistenceKeyOverride = null,
    suppressGameStatusSync = false,
    suppressRewardAlerts = false,
    trackingDateOverride = null,
  } = options;

  // Auth
  const { isSignedIn } = useUser();
  const { getToken, userId: clerkUserId } = useAuth();
  const identityOwner = `${getUserId()}:${isSignedIn ? clerkUserId : 'anonymous'}`;
  const identityOwnerRef = useRef(identityOwner);
  identityOwnerRef.current = identityOwner;
  const currentGameRef = useRef(gameSlug);
  currentGameRef.current = gameSlug;
  const [allowanceState, setAllowanceState] = useState(null);
  const hintAllowance = allowanceState?.owner === identityOwner
    && allowanceState?.value?.game_slug === gameSlug ? allowanceState.value : null;
  const receiveHintAllowance = useCallback((response, owner) => {
    if (identityOwnerRef.current !== owner || currentGameRef.current !== gameSlug) return;
    // A later mount must not reuse an allowance snapshot from before this event.
    for (const key of sharedSessionInitPromises.keys()) {
      if (key.startsWith(`${gameSlug}:`)) sharedSessionInitPromises.delete(key);
    }
    const value = response?.hint_allowance;
    if (value?.game_slug !== gameSlug) return;
    setAllowanceState((previous) => {
      if (previous?.owner === owner && previous.value.game_slug === gameSlug && previous.value.used > value.used) return previous;
      return { owner, value };
    });
  }, [gameSlug]);
  
  // Contexts
  const { updateBalance, setGettonciniData } = useGettoncini();
  const { setLearnedTermsData } = useLearnedTerms();
  const { setStellineData } = useStelline();
  const { setStreakData } = useStreak();
  const { queueAlert } = useAlertQueueActions();
  const { requestOffer, cancelOffer } = useGameStartAccountOffer();
  const offerSessionRef = useRef(null);
  const { queueResourceCelebration, queueStellineCelebration, queueStreakCelebration } = useResourceCelebration();
  const { handleStreakFromResponse } = useGameStreak();
  const { isAdBreakActive } = useAds();
  const { getPuzzleInstanceId, fetchPuzzleInstance, isLoading: isPuzzleInstancesLoading } = usePuzzleInstances();
  const { updateGameStatus } = useGameStatusesContext();
  const { checkDailyCircuit } = useMilestones();

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [puzzleConfig, setPuzzleConfig] = useState(null);
  const [solutions, setSolutions] = useState(null); // Solutions (only available when game is complete)
  const [solutionsAvailable, setSolutionsAvailable] = useState(false);
  const [latestAppResources, setLatestAppResources] = useState(null);
  const [latestCompletionInfo, setLatestCompletionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEventPending, setIsEventPending] = useState(false);
  
  // Track if user just won during this session (for celebration animation)
  // This is different from `won` which is true for any won game, including returning to one
  const [justWon, setJustWon] = useState(false);
  
  // Track if user just lost during this session (for solution reveal animation)
  // This is different from `lost` which is true for any lost game, including returning to one
  const [justLost, setJustLost] = useState(false);

  // Track if game completion has been triggered to prevent duplicates
  const completionTriggeredRef = useRef(false);
  const gameStartTimeRef = useRef(null);
  
  // Track if session has been initialized to prevent re-initialization
  const hasInitializedRef = useRef(false);
  const initKeyRef = useRef(null);
  const initInFlightRef = useRef(new Map());
  
  // Store callback in ref to avoid dependency issues
  const deriveStatusRef = useRef(deriveStatusFromLocalStorage);
  deriveStatusRef.current = deriveStatusFromLocalStorage;

  // Derived state
  const effectiveTrackingDate = trackingDateOverride || date;
  const status = sessionState?.status || GameStatus.NOT_STARTED;
  const gameOver = isGameOver(status);
  const won = status === GameStatus.WON;
  const lost = status === GameStatus.LOST;
  useGameSession(gameSlug, effectiveTrackingDate, gameStartTimeRef.current, gameOver);

  const localStorageOptions = persistenceKeyOverride
    ? { storageKey: persistenceKeyOverride }
    : undefined;
  const readStoredState = useCallback(() => getLocalData(gameSlug, date, localStorageOptions), [gameSlug, date, localStorageOptions]);
  const writeStoredState = useCallback((data) => {
    setLocalData(gameSlug, date, data, localStorageOptions);
  }, [gameSlug, date, localStorageOptions]);

  useEffect(() => () => {
    if (offerSessionRef.current) {
      cancelOffer(offerSessionRef.current, 'game_left');
      offerSessionRef.current = null;
    }
  }, [gameSlug, date, puzzleInstanceIdOverride, persistenceKeyOverride, cancelOffer]);

  // Initialize session
  useEffect(() => {
    // Wait for puzzle instances cache to be ready
    if (isPuzzleInstancesLoading) return;

    // Create a unique key for this game+date combination
    const initKey = `${gameSlug}:${date}:${puzzleInstanceIdOverride || 'default'}:${persistenceKeyOverride || 'default'}:${identityOwner}`;
    
    // Skip if we've already initialized for this exact game+date
    if (hasInitializedRef.current && initKeyRef.current === initKey) {
      return;
    }

    let isMounted = true;
    
    const getOrCreateInitPromise = () => {
      const inFlight = initInFlightRef.current.get(initKey);
      if (inFlight) {
        return inFlight;
      }

      const sharedInitKey = `${initKey}:${useServerState ? 'server' : 'local'}:${isSignedIn ? 'signed' : 'anon'}`;
      const promise = getOrCreateSharedSessionInitPromise(sharedInitKey, async () => {
        const token = isSignedIn
          ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
          : null;

        // Get puzzle instance ID from cache
        let puzzleInstanceId = puzzleInstanceIdOverride || getPuzzleInstanceId(gameSlug, date);

        // If not in cache, attempt to fetch it (for past dates beyond 30 days)
        if (!puzzleInstanceId && !puzzleInstanceIdOverride) {
          puzzleInstanceId = await fetchPuzzleInstance(gameSlug, date);
        }

        if (!puzzleInstanceId) {
          throw new Error(`Puzzle not found for ${gameSlug} on ${date}`);
        }

        // Check for existing localStorage data (for on-demand migration)
        const existingLocalData = readStoredState();
        const hasLocalData = existingLocalData && Object.keys(existingLocalData).length > 0;

        if (useServerState) {
          // Include localStorage data for on-demand migration if it exists
          const sessionResult = await createOrGetSession(
            puzzleInstanceId,
            {
              local_state: hasLocalData ? existingLocalData : null,
            },
            token
          );

          if (!sessionResult.success) {
            throw new Error(sessionResult.message || sessionResult.error || 'Failed to create session');
          }

          return {
            mode: 'server',
            sessionResult,
          };
        }

        // Fallback: use localStorage only
        const deriveStatus = deriveStatusRef.current;
        const derivedStatus = deriveStatus
          ? deriveStatus(existingLocalData)
          : GameStatus.NOT_STARTED;

        return {
          mode: 'local',
          localData: existingLocalData,
          derivedStatus,
        };
      });

      initInFlightRef.current.set(initKey, promise);
      promise.finally(() => {
        if (initInFlightRef.current.get(initKey) === promise) {
          initInFlightRef.current.delete(initKey);
        }
      });
      return promise;
    };

    const initSession = async () => {
      setIsLoading(true);
      setError(null);
      completionTriggeredRef.current = false;
      setLatestCompletionInfo(null);
      setJustWon(false);
      setJustLost(false);

      try {
        const initResult = await getOrCreateInitPromise();
        if (!isMounted || identityOwnerRef.current !== identityOwner) return;

        // Create or get session
        if (initResult.mode === 'server') {
          const { sessionResult } = initResult;
          receiveHintAllowance(sessionResult, identityOwner);
          setSessionId(sessionResult.session_id);
          setSessionState(sessionResult.state);
          setPuzzleConfig(sessionResult.puzzle_config);
          setSolutions(sessionResult.solutions || null);
          setSolutionsAvailable(Boolean(sessionResult.solutions_available || sessionResult.solutions));
          setLatestAppResources(sessionResult.app_resources || null);
          if (!suppressGameStatusSync && sessionResult.state?.status === GameStatus.IN_PROGRESS) {
            updateGameStatus(gameSlug, date, 'in_progress');
          } else if (
            !suppressGameStatusSync && (
            sessionResult.state?.status === GameStatus.WON
            || sessionResult.state?.status === GameStatus.LOST
          )) {
            updateGameStatus(gameSlug, date, 'finished');
          }
          completionTriggeredRef.current = isGameOver(sessionResult.state?.status);

          const sessionGettoncini = extractGettonciniResource(sessionResult);
          const sessionLearnedTerms = extractLearnedTermsResource(sessionResult);
          const sessionStelline = extractStellineResource(sessionResult);
          const sessionStreak = extractStreakResource(sessionResult);

          if (sessionGettoncini) {
            setGettonciniData(sessionGettoncini);
          } else if (typeof sessionResult?.app_resources?.gettoncini?.balance === 'number') {
            updateBalance(sessionResult.app_resources.gettoncini.balance);
          }

          if (sessionLearnedTerms) {
            setLearnedTermsData(sessionLearnedTerms);
          }

          if (sessionStelline) {
            setStellineData(sessionStelline);
          }

          if (sessionStreak) {
            setStreakData(mapStreakResourceToState(sessionStreak));
          }

          if (!suppressRewardAlerts && isSignedIn) {
            queueResourceAlertsFromPayload({
              queueAlert,
              payload: sessionResult,
              isAnonymous: !isSignedIn,
              firstAlertDelayMs: 1000,
              signedInDurationMs: 8000,
              anonymousDurationMs: 8000,
              streakDurationMs: 5000,
            });
          }
          
          // Mark as initialized
          hasInitializedRef.current = true;
          initKeyRef.current = initKey;
          
          // Store session reference in localStorage for future reference
          writeStoredState({
            ...sessionResult.state,
            _session_id: sessionResult.session_id,
          });

          if (!isSignedIn && !suppressRewardAlerts) {
            offerSessionRef.current = sessionResult.session_id;
          }

          // Log if migration occurred
          if (sessionResult.migrated_from_local) {
            if (isGameOver(sessionResult.state?.status)) invalidatePlayerGameStats(gameSlug, sessionResult.session_id);
            console.log(`[Session] Migrated localStorage data for ${gameSlug}/${date}`);
          }

          // Track analytics
          const startTime = Date.now();
          gameStartTimeRef.current = startTime;
          
          if (sessionResult.is_new_session && !sessionResult.migrated_from_local) {
            // Brand new game
            trackGameStart(gameSlug, effectiveTrackingDate);
          } else if (sessionResult.state.status === GameStatus.IN_PROGRESS) {
            // Resuming (either from server or from migrated localStorage)
            trackGameResume(gameSlug, effectiveTrackingDate, sessionResult.state);
          }
        } else {
          const { localData, derivedStatus } = initResult;
          setSessionState({ ...localData, status: derivedStatus });
          setSolutionsAvailable(false);
          completionTriggeredRef.current = isGameOver(derivedStatus);
          gameStartTimeRef.current = localData?.gameStartTime || Date.now();
          
          // Mark as initialized
          hasInitializedRef.current = true;
          initKeyRef.current = initKey;
        }

      } catch (err) {
        console.error('Session initialization failed:', err);
        if (isMounted) {
          setError(err.message);
          // Fallback to localStorage
          const localData = readStoredState();
          if (localData && Object.keys(localData).length > 0) {
            const deriveStatus = deriveStatusRef.current;
            const derivedStatus = deriveStatus 
              ? deriveStatus(localData)
              : GameStatus.NOT_STARTED;
            setSessionState({ ...localData, status: derivedStatus });
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initSession();

    return () => {
      isMounted = false;
    };
  }, [
    gameSlug,
    date,
    isSignedIn,
    identityOwner,
    receiveHintAllowance,
    useServerState,
    isPuzzleInstancesLoading,
    getToken,
    getPuzzleInstanceId,
    fetchPuzzleInstance,
    updateBalance,
    setGettonciniData,
    setLearnedTermsData,
    setStellineData,
    setStreakData,
    queueAlert,
    requestOffer,
    puzzleInstanceIdOverride,
    persistenceKeyOverride,
    readStoredState,
    writeStoredState,
    effectiveTrackingDate,
    suppressGameStatusSync,
    suppressRewardAlerts,
  ]);

  const refreshHintAllowance = useCallback(async () => {
    if (!sessionId || !useServerState) return;
    const owner = identityOwner;
    const token = isSignedIn
      ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null) : null;
    const response = await getSession(sessionId, token);
    if (identityOwnerRef.current !== owner || currentGameRef.current !== gameSlug) return;
    if (response?.hint_allowance) receiveHintAllowance(response, owner);
    else setAllowanceState(null);
    return response;
  }, [sessionId, useServerState, identityOwner, gameSlug, isSignedIn, getToken, receiveHintAllowance]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshHintAllowance(); };
    window.addEventListener('focus', refreshHintAllowance);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshHintAllowance);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshHintAllowance]);

  /**
   * Send a gameplay event and update state.
   * 
   * @param {string} eventType - Event type (e.g., 'submit', 'guess')
   * @param {object} eventData - Event-specific data
   * @returns {Promise<{success: boolean, result?: object, state?: object, error?: string, message?: string}>}
   */
  const dispatchEvent = useCallback(async (eventType, eventData = {}) => {
    if (isAdBreakActive) {
      return { success: false, error: 'AD_BREAK_ACTIVE', message: 'Attendi la fine della pubblicita.' };
    }

    if (!sessionId) {
      console.error('Cannot dispatch event: no session');
      return { success: false, error: 'NO_SESSION', message: 'Sessione non inizializzata' };
    }

    setIsEventPending(true);
    if (eventType !== 'set_difficulty') cancelOffer(sessionId);
    
    try {
      const token = isSignedIn 
        ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
        : null;

      const response = await sendGameplayEvent(
        sessionId,
        eventType,
        eventData,
        token,
        sessionState?.seq // Optional optimistic concurrency
      );

      if (identityOwnerRef.current !== identityOwner) {
        return { success: false, error: 'STALE_IDENTITY' };
      }
      receiveHintAllowance(response, identityOwner);

      const responseGettoncini = extractGettonciniResource(response);
      const responseLearnedTerms = extractLearnedTermsResource(response);
      const responseStelline = extractStellineResource(response);
      const responseStreak = extractStreakResource(response);
      const responseStatus = response.success ? response.state?.status : null;
      const isCompletionResponse = response.success
        && isGameOver(responseStatus)
        && !completionTriggeredRef.current;
      const completionAlerts = isCompletionResponse ? buildResourceAlertsFromPayload({
        payload: response,
        isAnonymous: !isSignedIn,
        includeStreakAlerts: true,
      }) : [];
      const stellineAlert = completionAlerts.find((alert) => alert.type === 'stelline');
      const learnedTermsAlert = completionAlerts.find((alert) => alert.type === 'learned_terms');
      const streakAlert = completionAlerts.find((alert) => alert.type === 'streak');
      const shouldCelebrateStelline = !suppressRewardAlerts
        && isSignedIn
        && isCompletionResponse
        && Number(stellineAlert?.actualDelta) > 0;
      const shouldCelebrateLearnedTerms = !suppressRewardAlerts
        && isSignedIn
        && isCompletionResponse
        && Number(learnedTermsAlert?.actualDelta) > 0;
      const shouldCelebrateStreak = !suppressRewardAlerts
        && isCompletionResponse
        && !!streakAlert;
      const excludedResourceTypes = [
        ...((shouldCelebrateStelline || !isSignedIn) ? ['stelline'] : []),
        ...((shouldCelebrateLearnedTerms || !isSignedIn) ? ['learned_terms'] : []),
      ];
      const shouldSuppressAnonymousCompletionStelline = !isSignedIn
        && isCompletionResponse
        && responseStelline;
      const shouldSuppressAnonymousCompletionLearnedTerms = !isSignedIn
        && isCompletionResponse
        && responseLearnedTerms;
      setLatestAppResources(response.app_resources || null);

      // Always trust backend balance when present (applies to both success and failure)
      if (responseGettoncini && typeof responseGettoncini.balance === 'number') {
        setGettonciniData(responseGettoncini);
      }

      if (
        responseLearnedTerms
        && !shouldSuppressAnonymousCompletionLearnedTerms
      ) {
        setLearnedTermsData(responseLearnedTerms);
      }

      if (
        responseStelline
        && !shouldSuppressAnonymousCompletionStelline
      ) {
        setStellineData(responseStelline);
      }

      if (responseStreak && (!response.success || isCompletionResponse)) {
        setStreakData(mapStreakResourceToState(responseStreak));
      }

      if (response.success) {
        const newStatus = responseStatus;
        const isFirstCompletion = isGameOver(newStatus) && !completionTriggeredRef.current;

        if (isFirstCompletion) {
          completionTriggeredRef.current = true;
          if (!suppressGameStatusSync && !suppressRewardAlerts && !puzzleInstanceIdOverride
            && !persistenceKeyOverride && !trackingDateOverride) {
            checkDailyCircuit({ gameSlug, date, getPuzzleInstanceId });
          }
          invalidatePlayerGameStats(gameSlug, sessionId);
          if (!isSignedIn && !suppressRewardAlerts) {
            requestOffer({ sessionId, gameSlug, delayMs: Math.max(1000,
              getCompletionAlertDelayMs(gameSlug, newStatus, response.result, response.state)) });
          }

          const completionTimestamp = Date.now();
          setSessionState(response.state);
          writeStoredState({
            ...readStoredState(),
            ...response.state,
          });

          if (!suppressGameStatusSync) {
            updateGameStatus(gameSlug, date, 'finished');
          }

          const trackingStartTime = getTrackingStartTimeAtCompletion(
            gameStartTimeRef.current,
            completionTimestamp,
          );
          const durationSeconds = Math.max(
            0,
            Math.round((completionTimestamp - trackingStartTime) / 1000)
          );
          setLatestCompletionInfo({
            completedAtMs: completionTimestamp,
            durationSeconds,
            sessionSeq: typeof response.state?.seq === 'number' ? response.state.seq : null,
            resultStatus: newStatus || null,
            didWin: newStatus === GameStatus.WON,
          });

          if (newStatus === GameStatus.WON) {
            trackGameCompletion(gameSlug, effectiveTrackingDate, trackingStartTime, response.state, { won: true });
            setJustWon(true);
          } else {
            trackGameOver(gameSlug, effectiveTrackingDate, trackingStartTime, response.state);
            setJustLost(true);
          }

          if (responseStreak) {
            if (suppressRewardAlerts) {
              setStreakData(mapStreakResourceToState(responseStreak));
            } else {
              const initialDelayMs = getCompletionAlertDelayMs(
                gameSlug,
                newStatus,
                response.result,
                response.state,
              );
              const rewardSchedule = buildCompletionResourceSchedule({
                initialDelayMs,
                shouldWaitForWinCelebration: newStatus === GameStatus.WON,
                shouldCelebrateStreak,
                shouldCelebrateStelline,
                shouldCelebrateLearnedTerms,
              });
              if (shouldCelebrateStreak) {
                queueStreakCelebration({
                  delta: 1,
                  counterBefore: getStreakCounterBefore(responseStreak.current_streak),
                  rewardAlert: streakAlert,
                  delayMs: rewardSchedule.flightDelays.streak,
                });
              }
              if (shouldCelebrateStelline) {
                queueStellineCelebration({
                  delta: stellineAlert.delta,
                  rewardAlert: stellineAlert,
                  delayMs: rewardSchedule.flightDelays.stelline,
                });
              }
              if (shouldCelebrateLearnedTerms) {
                queueResourceCelebration('learned_terms', {
                  delta: learnedTermsAlert.delta,
                  rewardAlert: learnedTermsAlert,
                  delayMs: rewardSchedule.flightDelays.learnedTerms,
                });
              }
              handleStreakFromResponse(
                gameSlug,
                date,
                responseStreak,
                responseGettoncini,
                (shouldCelebrateStelline || !isSignedIn) ? null : responseStelline,
                (shouldCelebrateLearnedTerms || !isSignedIn) ? null : responseLearnedTerms,
                {
                  initialDelayMs,
                  firstAlertDelayMs: rewardSchedule.alertDelayMs,
                  skipStreakDataSync: shouldCelebrateStreak,
                  payload: response,
                  excludedResourceTypes,
                },
              );
            }
          } else if (!suppressRewardAlerts) {
            const rewardSchedule = buildCompletionResourceSchedule({
              shouldWaitForWinCelebration: newStatus === GameStatus.WON,
              shouldCelebrateStreak,
              shouldCelebrateStelline,
              shouldCelebrateLearnedTerms,
            });
            if (
              shouldCelebrateStreak
              || shouldCelebrateStelline
              || shouldCelebrateLearnedTerms
              || (!isSignedIn && (responseStelline || responseLearnedTerms))
            ) {
              if (shouldCelebrateStreak) {
                queueStreakCelebration({
                  delta: 1,
                  counterBefore: getStreakCounterBefore(streakAlert.streak),
                  rewardAlert: streakAlert,
                  delayMs: rewardSchedule.flightDelays.streak,
                });
              }
              if (shouldCelebrateStelline) {
                queueStellineCelebration({
                  delta: stellineAlert.delta,
                  rewardAlert: stellineAlert,
                  delayMs: rewardSchedule.flightDelays.stelline,
                });
              }
              if (shouldCelebrateLearnedTerms) {
                queueResourceCelebration('learned_terms', {
                  delta: learnedTermsAlert.delta,
                  rewardAlert: learnedTermsAlert,
                  delayMs: rewardSchedule.flightDelays.learnedTerms,
                });
              }
            }
            queueResourceAlertsFromPayload({
              queueAlert,
              payload: response,
              excludedResourceTypes,
              isAnonymous: !isSignedIn,
              firstAlertDelayMs: rewardSchedule.alertDelayMs,
            });
          }

          if (response.result?.solutions) {
            setSolutions(response.result.solutions);
            setSolutionsAvailable(true);
          }
        } else {
          setSessionState(response.state);
          writeStoredState({
            ...readStoredState(),
            ...response.state,
          });

          if (!suppressGameStatusSync && newStatus === GameStatus.IN_PROGRESS) {
            updateGameStatus(gameSlug, date, 'in_progress');
          }
          if (!suppressRewardAlerts) {
            queueResourceAlertsFromPayload({
              queueAlert,
              payload: response,
              isAnonymous: !isSignedIn,
            });
          }
        }

        return { success: true, result: response.result, state: response.state, hint_allowance: response.hint_allowance };
      } else {
        // Handle known error codes
        logGameEvent('event_error', gameSlug, effectiveTrackingDate, {
          event_type: eventType, 
          error: response.error 
        });
        return {
          success: false,
          error: response.error,
          message: response.message,
          gettoncini: responseGettoncini,
          hint_allowance: response.hint_allowance,
        };
      }
    } catch (err) {
      console.error('Event dispatch failed:', err);
      logGameEvent('event_error', gameSlug, effectiveTrackingDate, {
        event_type: eventType,
        error: 'NETWORK_ERROR',
      });
      return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
    } finally {
      setIsEventPending(false);
    }
  }, [sessionId, sessionState, isSignedIn, identityOwner, receiveHintAllowance, gameSlug, date, getToken, updateBalance, setGettonciniData, setLearnedTermsData, setStellineData, setStreakData, handleStreakFromResponse, updateGameStatus, queueAlert, queueResourceCelebration, queueStellineCelebration, queueStreakCelebration, isAdBreakActive, readStoredState, writeStoredState, suppressGameStatusSync, suppressRewardAlerts, effectiveTrackingDate, cancelOffer, requestOffer, checkDailyCircuit, getPuzzleInstanceId, puzzleInstanceIdOverride, persistenceKeyOverride, trackingDateOverride]);

  /**
   * Dispatch a hint using the server's anonymous allowance or account payment.
   * Invokes the authentication callback when an anonymous hint requires an account.
   * 
   * @param {string} hintType - Hint type (e.g., 'hint_reveal_theme', 'hint_definition')
   * @param {object} hintData - Hint-specific data
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{success: boolean, hint_data?: object, error?: string, message?: string}>}
   */
  const dispatchHintEvent = useCallback(async (hintType, hintData = {}, onNotAuthenticated = null) => {
    cancelOffer(sessionId);
    if (!isSignedIn && !hasFreeAnonymousHints(hintAllowance, gameSlug, isSignedIn)) {
      if (onNotAuthenticated) {
        onNotAuthenticated();
      }
      return { success: false, error: 'NOT_AUTHENTICATED', message: 'Devi accedere per usare i suggerimenti' };
    }

    const response = await dispatchEvent(hintType, hintData);
    const normalized = normalizeHintResponse(response);
    if (normalized?.error === 'NOT_AUTHENTICATED') onNotAuthenticated?.(normalized.hint_allowance);
    
    if (normalized.success && normalized.hint_data != null) {
      logGameEvent('hint_used', gameSlug, effectiveTrackingDate, { type: hintType });
    } else if (!normalized.success) {
      if (normalized.error === 'INSUFFICIENT_BALANCE') {
        trackGameHintInsufficientBalance({
          gameSlug,
          gameDate: effectiveTrackingDate,
          hintType,
          source: 'server_response',
          balanceBefore: typeof normalized.gettoncini?.balance === 'number' ? normalized.gettoncini.balance : null,
          hintCost: null,
          modalName: 'aiutini',
        });
      }
      logGameEvent('hint_failed', gameSlug, effectiveTrackingDate, {
        type: hintType,
        error: normalized.error || 'UNKNOWN',
      });
    }
    
    return normalized;
  }, [isSignedIn, hintAllowance, dispatchEvent, gameSlug, effectiveTrackingDate, trackGameHintInsufficientBalance, cancelOffer, sessionId]);

  /**
   * Manually update local state (for optimistic updates or localStorage-only mode).
   * Use sparingly - prefer dispatchEvent for server-synced state.
   * 
   * @param {object} updates - State updates to apply
   */
  const updateLocalState = useCallback((updates) => {
    cancelOffer(sessionId);
    setSessionState(prev => {
      const newState = { ...prev, ...updates };
      writeStoredState({
        ...readStoredState(),
        ...newState,
      });
      return newState;
    });
  }, [readStoredState, writeStoredState, cancelOffer, sessionId]);

  return {
    // Session info
    sessionId,
    puzzleConfig,
    solutions, // Only available when game is complete (won/lost)
    solutionsAvailable,
    latestAppResources,
    latestCompletionInfo,
    
    // State
    state: sessionState,
    status,
    gameOver,
    won,
    lost,
    justWon: initKeyRef.current === `${gameSlug}:${date}:${puzzleInstanceIdOverride || 'default'}:${persistenceKeyOverride || 'default'}:${identityOwner}` && justWon,
    justLost: initKeyRef.current === `${gameSlug}:${date}:${puzzleInstanceIdOverride || 'default'}:${persistenceKeyOverride || 'default'}:${identityOwner}` && justLost,
    
    // Loading/error
    isLoading,
    error,
    isEventPending,
    
    // Actions
    dispatchEvent,
    dispatchHintEvent,
    updateLocalState,
    
    // Utilities
    isSignedIn,
    hintAllowance,
    refreshHintAllowance,
  };
}
